<template>
  <b-modal
    id="modal-bucket-view"
    ref="modal"
    v-model="open"
    hide-footer
    no-close-on-backdrop
    centered
    size="xl"
    :title="'Bucket View - ' + bucketName"
  >
    <FilterCard :applied-filter-count="appliedFilterCount">
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Status"
            label-for="status"
          >
            <v-select
              v-model="selectedStatusFilters"
              :options="statusOptions"
              :reduce="option => option.key"
              label="name"
              placeholder="Select Status"
              class="status-dropdown"
              multiple
              deselect-from-dropdown
            >
              <template
                v-slot:option="option"
                class="text-truncate"
              >
                {{ option.name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Bag ID"
            label-for="sort_bag"
          >
            <v-select
              v-model="filters['sort_bag']"
              :options="bags"
              :reduce="option => option.id"
              label="name"
              placeholder="Select Bag"
            >
              <template v-slot:option="option">
                {{ option.name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-1">
        <b-col>
          <filter-buttons
            ref="filterButtons"
            :filter-data="filters"
            :apply-filters="applyFilters"
            :clear-filters="clearFilters"
          />
        </b-col>
      </b-row>
    </FilterCard>
    <b-overlay
      id="overlay-background"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <b-table
        striped
        hover
        responsive
        :per-page="perPage"
        :items="items"
        :fields="computedTableFields"
        :json_fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        sticky-header="100vh"
      >
        <template #empty>
          <TableDataFetching
            :rows="items"
            :data-loading="dataLoading"
          />
        </template>
        <template #cell(status)="data">
          <div>
            <order-status-badge :status-key="data.item.order_current_status.key" />
          </div>
        </template>
        <template #cell(waybill_number)="data">
          <div
            class="font-weight-bolder ml-1 text-primary"
            @click="onClickViewOrder(data.item.id)"
          >
            {{ data.value }}
          </div>
        </template>
        <template #cell(sorting_bag)="data">
          <div>
            {{ data.item.sorting_bag.display_name }}
          </div>
        </template>
        <template #cell(sorted_date)="data">
          {{ __dateTimeFormatter(data.value) }}
        </template>
        <template #cell(customer_phone)="data">
          <div>{{ data.item.customer_phone }}</div>
          <div>{{ data.item.customer_secondary_phone }}</div>
        </template>
        <template #cell(cod_value)="data">
          {{ __numberWithCommas(data.value, true) }}
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row
          cols="12"
        >
          <b-col
            class="d-flex align-items-center justify-content-sm-start"
            md="4"
          >
            <label class="width-75">Per page</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block width-125"
            />
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-sm-center"
            md="4"
          >
            <div><row class="d-flex flex-row justify-content-center align-items-center">
              <ReportTableButtons
                :items="items"
                :json_fieldz="json_fields"
                :name="`${downloadReportName} - Page ${page}.xls`"
                :bulkname="`${downloadReportName}.xls`"
                :fetch="fetchBucketOrdersNoPagination"
                @refresh="fetchBucketOrders"
              />
              <div class="d-flex justify-content-center pl-1">
                <b-button
                  v-if="isDestination && hasKey29AndKey35 && filters['sort_bag'] !== null && filtered"
                  v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                  type="submit"
                  size="md"
                  class="custom-button-color vh-5"
                  :disabled="loadingResponse"
                  @click="handleDispatch()"
                >
                  <feather-icon
                    icon="TruckIcon"
                    class="mr-50"
                  />
                  {{ loadingResponse ? 'Dispatching...' : 'Dispatch & Print Bag ID' }}
                </b-button>
              </div>
            </row>
            </div>
          </b-col>
          <!-- Pagination -->
          <b-col
            class="d-flex align-items-center justify-content-sm-end"
            md="4"
          >
            <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
            <b-pagination
              v-model="page"
              :per-page="perPage"
              :total-rows="(meta.total)? meta.total : 0"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              next-class="next-item"
              prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-overlay>
    <view-order-modal ref="viewOrderModal" />
  </b-modal>
</template>

<script>
import {
  BModal,
  BCol,
  BRow,
  BOverlay,
  BTable,
  VBModal,
  BPagination,
  BButton,
  BFormGroup,
} from 'bootstrap-vue'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import OrderStatusBadge from '@/@core/components/status/OrderStatusBadge.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'

const sortingRepository = RepositoryFactory.get('sorting')
const orderRepository = RepositoryFactory.get('order')

function initialState() {
  return {
    open: false,
    bucketId: null,
    modalLoading: false,
    fields: {},
    json_fields: {
      'Waybill Number': 'waybill_number',
      Status: {
        field: 'order_current_status.name',
        callback: value => value || 'N/A',
      },
      'Bag ID': {
        field: 'sorting_bag.display_name',
        callback: value => value || 'N/A',
      },
      'Customer Name': {
        field: 'customer_name',
        callback: value => `"${value}"`,
      },
      'Customer Email': {
        field: 'customer_email',
        callback: value => `"${value}"`,
      },
      'Customer Address': {
        field: 'customer_address',
        callback: value => `"${value}"`,
      },
      COD: {
        field: 'cod_value',
        callback: value => value?.toFixed(2) ?? '0.00',
      },
      Weight: {
        field: 'weight',
        callback: value => value?.toFixed(2) ?? '0.00',
      },
      'Customer Phone': {
        field: 'customer_phone',
        callback: value => `"${value}"`,
      },
      'Customer Secondary Phone': {
        field: 'customer_secondary_phone',
        callback: value => `"${value}"`,
      },
    },
    items: [],
    dataLoading: false,
    perPage: 500,
    pageOptions: [200, 250, 300, 350, 400, 450, 500],
    page: 1,
    meta: {
      from: 0,
      to: 0,
      total: 0,
    },
    filtered: false,
    tablefields: [
      {
        key: 'waybill_number',
        label: 'Waybill Number',
        sortable: true,
        tdClass: 'text-left',
      },
      {
        key: 'status',
        label: 'Status',
        sortable: false,
        thClass: 'text-center',
        tdClass: 'text-center',
      },
      {
        key: 'sorting_bag',
        label: 'Bag ID',
        sortable: false,
        thClass: 'text-center',
        tdClass: 'text-center',
      },
      {
        key: 'customer_name',
        label: 'Customer Name',
        sortable: true,
      },
      {
        key: 'customer_email',
        label: 'Customer Email',
        sortable: true,
      },
      {
        key: 'customer_address',
        label: 'Customer Address',
        sortable: false,
      },
      {
        key: 'cod_value',
        label: 'COD',
        sortable: false,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'weight',
        label: 'Weight',
        sortable: false,
        thClass: 'text-right',
        tdClass: 'text-right',
      },
      {
        key: 'customer_phone',
        label: 'Customer Phone',
        sortable: false,
        thClass: 'text-left',
        tdClass: 'text-left',
      },
      {
        key: 'customer_secondary_phone',
        label: 'Customer Secondary Phone',
        sortable: false,
        thClass: 'text-left',
        tdClass: 'text-left',
      },
    ],
    sortBy: '',
    sortDesc: false,
    sortDirection: 'asc',
    filters: {},
    filter: null,
    statusOptions: [],
    bags: [],
    loadingResponse: false,
    selectedStatusFilters: [],
    downloadReportName: '',
    isDestination: false,
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BModal,
    BCol,
    BRow,
    BOverlay,
    BTable,
    BPagination,
    ViewOrderModal,
    vSelect,
    BButton,
    OrderStatusBadge,
    BFormGroup,
    FilterButtons,
    FilterCard,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return initialState()
  },
  computed: {
    // build the final fields array
    computedTableFields() {
      // clone the original
      const fields = [...this.tablefields]
      return fields
    },
    hasKey29AndKey35() {
      return this.selectedStatusFilters.includes('key_29') && this.selectedStatusFilters.includes('key_35')
    },
  },
  watch: {
    page() {
      this.fetchBucketOrders()
    },
    perPage() {
      this.fetchBucketOrders()
    },
    open(val) {
      if (val) {
        this.onModalShow()
      }
    },
    selectedStatusFilters: {
      async handler(val) {
        let keyList = ''
        val.forEach(key => {
          keyList += `${key},`
        })
        this.filters = {
          ...this.filters,
          status: keyList,
        }
      },
      deep: true,
    },
  },
  methods: {
    async openModal(bucketId, bucketName, bagName, isDestination, isReport = false, sortingString = '') {
      Object.assign(this.$data, initialState())
      this.bucketId = bucketId
      this.bucketName = bucketName
      this.bagName = bagName
      this.downloadReportName = `Bucket Orders ${this.bucketName}`
      this.isDestination = isDestination
      this.isReport = isReport
      this.sortingString = sortingString
      this.open = true
      this.readOrderStatuses()
      await this.fetchBagList()

      // Set default filter values
      this.selectedStatusFilters = ['key_29', 'key_35']
      const matchingBag = this.bags.find(bag => bag.name === bagName)
      if (matchingBag) {
        this.filters.sort_bag = matchingBag.id
      }
      this.$nextTick(() => {
        this.applyFilters()
        setTimeout(() => {
          if (this.$refs.filterButtons) {
            this.$refs.filterButtons.forceAppliedState()
          }
        }, 0)
      })
    },
    async onModalShow() {
      if (this.open && this.bucketId) {
        this.modalLoading = true
        try {
          await this.fetchBucketOrders()
        } catch (e) {
          this.convertAndNotifyError(e)
        } finally {
          this.modalLoading = false
        }
      }
    },
    async fetchBucketOrders() {
      if (!this.bucketId) return
      this.dataLoading = true
      this.items = []
      try {
        const { data } = await sortingRepository.getBucketOrdersById(this.page, this.perPage, this.bucketId, this.isReport, this.sortingString, this.filterQuery)
        this.items = data.data
        this.meta = data.meta
        this.filtered = this.filterQuery.length > 0
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchBucketOrdersNoPagination() {
      try {
        const { data } = (await sortingRepository.getBucketOrdersNoPagination(this.bucketId, this.$isReport, this.sortingString))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async fetchBagList() {
      this.dataLoading = true
      this.items = []
      try {
        const { data } = await sortingRepository.fetchBagList(this.bucketId, this.sortingString)
        this.bags = data.data.map(bag => ({
          id: bag.id,
          name: bag.display_name,
        }))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async readOrderStatuses() {
      this.statusOptions = localStorage.getItem('sorting_statuses')
        ? JSON.parse(localStorage.getItem('sorting_statuses')).filter(status => ['key_35', 'key_10', 'key_29'].includes(status.key))
        : (await orderRepository.getFullStatusList()).data.data.filter(status => ['key_35', 'key_10', 'key_29'].includes(status.key))
      if (!localStorage.getItem('sorting_statuses')) {
        localStorage.setItem('sorting_statuses', JSON.stringify(this.statusOptions))
      }
    },
    async onClickViewOrder(id) {
      this.$refs.viewOrderModal.openModal(id)
    },
    async handleDispatch() {
      this.loadingResponse = true
      const waybillNumbers = this.items.map(order => order.waybill_number)
      if (!waybillNumbers.length) {
        this.showErrorMessage('No waybills to dispatch.')
        this.loadingResponse = false
        return
      }
      try {
        const payload = {
          waybill_numbers: waybillNumbers,
          status_key: 'key_10', // example, adjust as needed
          sorting_id_string: this.sortingString,
          other_details: {
            destination_bucket_id: this.bucketId,
          },
        }
        await sortingRepository.orderBulkDispatchStatusUpdate(payload)
        this.showSuccessMessage('Orders dispatched successfully!')
        this.fetchBucketOrders()
        this.$router.push({
          name: 'print-bag',
          params: {
            bagId: this.filters.sort_bag,
          },
        })
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loadingResponse = false
    },
    applyFilters() {
      this.fetchBucketOrders()
      this.updateFilterCount()
    },
    clearFilters() {
      this.selectedStatusFilters = ''
      this.filters = {}
      this.$nextTick(() => {
        this.fetchBucketOrders()
      })
      this.updateFilterCount()
    },
  },
}
</script>

<style lang="scss">
@import '../../@core/scss/vue/libs/vue-select.scss';
.dispatch-button {
  background-color: #fff !important;
  border-color: rgb(28, 0, 152) !important;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  color: rgb(28, 0, 152) !important;
  font-weight: 500 !important;
  margin-left: 1rem;
}
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.dispatch-button:enabled {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
.custom-button-color-blur {
  background-color: #6da5ff !important;
  border-color: #6da5ff !important;
}
</style>
